import React, { useEffect, useMemo, useState } from "react";
import { Button, Caption1, makeStyles, Spinner } from "@fluentui/react-components";
import { useOfficeItem } from "../../../providers";
import { useAddInStore, useTokenStore } from "../../../hooks";
import { SpaceMeeting, UserProfile } from "../../../models";
import {
  msGraphService,
  showGettingStartedModal,
  startCadChatOnboardingProcess,
  supabaseApi,
} from "../../../services";
import { logger } from "../../../utils";
import { Settings } from "./Settings";
import { AddToExistingSpace } from "./AddToExistingSpace";
import MainViewButtons from "./MainViewButtons";
import { CreateSpace } from "./CreateSpace";
import MainViewEventDetails from "./MainEventDetails";
import { CADchatMeetingDetails } from "./CADchatMeetingDetails";
import { envUtils } from "../../../utils/envUtils";
import Onboarding from "./Onboarding";
import { About } from "./About";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    overflowY: "hidden",
  },
  body: {
    display: "flex",
    flexDirection: "column",
    padding: "20px",
    gap: "5px",
  },
  views: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  loading: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    height: "50vh",
    justifyContent: "center",
    gap: "10px",
  },
});

const Main = () => {
  const styles = useStyles();

  // indicates the current event
  const { event, officeService, isReady, isLoading, isFetching } = useOfficeItem();

  // indicates the current user is authenticated
  const isAuthenticated = useTokenStore((state) => state.isAuthenticated);

  // indicates that the we are trying to load the associated space meeting
  const [isLoadingMeeting, setIsLoadingMeeting] = useState<boolean>(false);

  // tracks the Outlook event id that has completed CADchat meeting lookup
  const [meetingLookupEventId, setMeetingLookupEventId] = useState<string | undefined>(undefined);

  // indicates the current space meeting associated with the event
  const meeting = useAddInStore<SpaceMeeting | undefined>((state) => state.meeting);

  // used to set the meeting in the state
  const setMeeting = useAddInStore((state) => state.setMeeting);

  // used to clear a stale meeting when the current Outlook event has no CADchat meeting
  const removeMeeting = useAddInStore((state) => state.removeMeeting);

  // indicates the current organizations this user has access to
  const organizations = useAddInStore((state) => state.organizations);

  // indicates the current organization id
  const selectedOrganizationId = useAddInStore((state) => state.selectedOrganizationId);

  // indicates the meeting should be added to an existing space
  const currentView = useAddInStore((state) => state.currentView);

  // used to update the current view
  const setCurrentView = useAddInStore((state) => state.setCurrentView);

  // used to indicate that another dialog is open
  const isDialogOpen = useAddInStore((state) => state.isDialogOpen);

  // used to indicate that another dialog is open
  const hasSeenHelp = useAddInStore((state) => state.hasSeenHelp);

  // used to set that the user has seen the help modal
  const setHasSeenHelp = useAddInStore((state) => state.setHasSeenHelp);

  useEffect(() => {
    if (!isAuthenticated || isLoading || isFetching) return undefined;
    else if (!event?.id) {
      setMeetingLookupEventId(undefined);
      setIsLoadingMeeting(false);
      removeMeeting();
      return undefined;
    }

    let isActive = true;
    const eventId = event.id;

    const getMeetingByEventId = async () => {
      try {
        setMeetingLookupEventId(undefined);
        setIsLoadingMeeting(true);

        logger.info("Fetching space meeting by event id:", eventId);

        const spaceMeeting = await supabaseApi.getSpaceMeetingByEventId(eventId);
        if (!spaceMeeting) throw new Error("No meeting found");
        if (!isActive) return;

        // set current meeting
        setMeeting(spaceMeeting);
      } catch (error) {
        if (!isActive) return;
        logger.warn("Error fetching space meeting:", error);
        removeMeeting();
      } finally {
        if (!isActive) return;
        setMeetingLookupEventId(eventId);
        setIsLoadingMeeting(false);
      }
    };

    getMeetingByEventId();

    return () => {
      isActive = false;
    };
  }, [isAuthenticated, isLoading, isFetching, event?.id]);

  useEffect(() => {
    if (isLoading || !isAuthenticated || !isReady) return;

    const checkPermissions = async () => {
      try {
        const hasPermission = await supabaseApi.hasPermissionInAnyOrganization("space.INSERT");
        if (hasPermission && !selectedOrganizationId && currentView === "default") {
          setCurrentView("settings");
        } else if (currentView !== "settings" && !hasSeenHelp) {
          await showGettingStartedModal();
          setHasSeenHelp(true);
        }
      } catch (error) {
        logger.error("Error checking permissions:", error);
      }
    };

    // guard against running on first render before anything is ready
    checkPermissions();
  }, [currentView, isAuthenticated, isLoading, isReady, selectedOrganizationId, hasSeenHelp]);

  useEffect(() => {
    if (!isAuthenticated || isDialogOpen) return;

    const initMsGraphService = async () => {
      try {
        if (msGraphService.canInitializeClient) {
          logger.info("Initializing MS Graph Service....");

          await msGraphService.initializeClient();
        }
      } catch (error) {
        // TODO: Need better error handling here since this is a critical part of the app
        logger.error("Error initializing MS Graph Service:", error);
      }
    };

    Office.onReady(() => initMsGraphService());
  }, [isAuthenticated, isDialogOpen]);

  const isMeetingLookupPending =
    isAuthenticated &&
    !isLoading &&
    !isFetching &&
    !!event?.id &&
    meetingLookupEventId !== event.id;

  // if the user is not authenticated or the app is not ready, show a loading spinner
  if (!isAuthenticated || isLoading || isFetching || isLoadingMeeting || isMeetingLookupPending) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" />
        <Caption1>
          Loading {event?.id && !isLoading ? "Meeting Information" : "CADchat Add-In"}
        </Caption1>
      </div>
    );
  }

  // if there is already a meeting we should show the meeting details
  if (meeting) {
    return (
      <div className={styles.root}>
        <CADchatMeetingDetails />
        <MainViewEventDetails />
      </div>
    );
  }

  // if the user clicks the settings button, show the settings view
  if (currentView === "settings") {
    return (
      <div className={styles.root}>
        <Settings />
      </div>
    );
  }

  // if the user is not onboarded we need to show the onboarding view
  if (currentView === "onboarding") {
    return (
      <div className={styles.root}>
        <Onboarding />
      </div>
    );
  }

  // if the user clicks the about button, show the about view
  if (currentView === "about") {
    return (
      <div className={styles.root}>
        <About />
      </div>
    );
  }

  // otherwise, show the main view with buttons that allow the user to create or add a meeting to and existing space
  return (
    <div className={styles.root}>
      {selectedOrganizationId && !meeting ? (
        <div className={styles.views}>
          {currentView === "default" ? (
            <MainViewButtons />
          ) : currentView === "create_space" ? (
            <CreateSpace />
          ) : currentView === "add_to_existing_space" ? (
            <AddToExistingSpace />
          ) : null}
        </div>
      ) : null}

      <MainViewEventDetails />
    </div>
  );
};

export default Main;
